/**
 * Share an event to the places people actually share events.
 *
 * Each platform gets its own button with its own mark, rather than one "Share This
 * Event" link and a URL field to copy by hand.
 *
 * A note on Instagram, because it is the one that cannot work like the others:
 * Instagram has no web share intent. There is no URL you can open that composes a
 * post with a link in it — the platform deliberately does not support link sharing
 * from the web, and any button claiming to do so either opens the app to nothing or
 * silently fails. So Instagram here copies the link and says so, which is the actual
 * workflow: copy, open the app, paste into a story or bio. Labelling it "Share to
 * Instagram" would be a lie about what the click does.
 */
import { useState } from "react";
import { Check, Copy, Facebook, Instagram, Linkedin, Mail } from "lucide-react";

/** X, which lucide has no current mark for — its icon is still the old bird. */
function XMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

type ShareTarget = {
  key: string;
  label: string;
  /** Screen-reader and tooltip text — says what the click actually does. */
  title: string;
  icon: React.ReactNode;
  /** Brand colour, used for the hover state only so the row stays calm at rest. */
  tint: string;
  href?: string;
  onClick?: () => void;
};

export default function SocialShareButtons({
  url,
  title,
  accentColor,
  onCopied,
}: {
  url: string;
  /** The event's title, used as the pre-filled post text where a platform takes one. */
  title: string;
  accentColor: string;
  /** Lets the host page raise its own notification on a successful copy. */
  onCopied?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /*
       * Clipboard API needs a secure context and permission. The textarea route is
       * the fallback that still works on an http origin or an older browser, and it
       * is why this does not simply surface the failure.
       */
      const field = document.createElement("textarea");
      field.value = url;
      field.setAttribute("readonly", "");
      field.style.position = "absolute";
      field.style.left = "-9999px";
      document.body.appendChild(field);
      field.select();
      document.execCommand("copy");
      document.body.removeChild(field);
    }
    setCopied(true);
    onCopied?.();
    setTimeout(() => setCopied(false), 2400);
  }

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const targets: ShareTarget[] = [
    {
      key: "linkedin",
      label: "LinkedIn",
      title: `Share "${title}" on LinkedIn`,
      icon: <Linkedin size={16} />,
      tint: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      title: `Share "${title}" on Facebook`,
      icon: <Facebook size={16} />,
      tint: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      key: "x",
      label: "X",
      title: `Share "${title}" on X`,
      icon: <XMark size={15} />,
      tint: "#E7E9EA",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      key: "instagram",
      label: "Instagram",
      // Says what it does. Instagram cannot accept a link from the web.
      title: "Instagram has no web link sharing — this copies the link to paste into the app",
      icon: <Instagram size={16} />,
      tint: "#E1306C",
      onClick: () => void copyLink(),
    },
    {
      key: "email",
      label: "Email",
      title: `Email a link to "${title}"`,
      icon: <Mail size={16} />,
      tint: "#94A3B8",
      href: `mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`I thought you'd be interested in this event:\n\n${title}\n${url}`)}`,
    },
  ];

  return (
    <div className="event-share">
      <ul className="event-share__row">
        {targets.map(target => {
          /*
            Icon only. The visible label is gone, but the name is not: `title` gives a
            hover tooltip and `aria-label` gives the accessible name, so the button is
            still identifiable without reading a platform logo. Dropping both would
            leave a screen reader announcing "button".
          */
          const inner = <span className="event-share__icon" aria-hidden="true">{target.icon}</span>;

          return (
            <li key={target.key}>
              {target.href ? (
                <a
                  className="event-share__button"
                  style={{ "--share-tint": target.tint } as React.CSSProperties}
                  href={target.href}
                  target="_blank"
                  /*
                    noopener is the one that matters: without it the opened tab gets a
                    handle on this window and can navigate it somewhere else.
                  */
                  rel="noopener noreferrer"
                  title={target.title}
                  aria-label={target.title}
                >
                  {inner}
                </a>
              ) : (
                <button
                  type="button"
                  className="event-share__button"
                  style={{ "--share-tint": target.tint } as React.CSSProperties}
                  onClick={target.onClick}
                  title={target.title}
                  aria-label={target.title}
                >
                  {inner}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        className="event-share__copy"
        style={{ "--share-tint": accentColor } as React.CSSProperties}
        onClick={() => void copyLink()}
        aria-live="polite"
      >
        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
        {copied ? "Link copied" : "Copy event link"}
      </button>

      <style>{`
        .event-share { display: grid; gap: .7rem; }
        .event-share__row {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: .4rem;
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .event-share__button {
          display: grid;
          place-items: center;
          width: 100%;
          aspect-ratio: 1;
          padding: 0;
          border: 1px solid rgba(255,255,255,.09);
          border-radius: .7rem;
          background: rgba(255,255,255,.03);
          color: #94A3B8;
          font-family: 'Space Grotesk', sans-serif;
          text-decoration: none;
          cursor: pointer;
          transition: border-color 160ms ease, background 160ms ease, color 160ms ease, transform 160ms ease;
        }
        /* Brand colour on hover only, so five logos do not shout at rest. */
        .event-share__button:hover,
        .event-share__button:focus-visible {
          border-color: var(--share-tint);
          background: color-mix(in srgb, var(--share-tint) 14%, transparent);
          color: var(--share-tint);
          transform: translateY(-1px);
        }
        .event-share__icon { display: grid; place-items: center; }

        .event-share__copy {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .45rem;
          width: 100%;
          min-height: 42px;
          padding: 0 .9rem;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: .7rem;
          background: rgba(255,255,255,.04);
          color: #CBD5E1;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
          cursor: pointer;
          transition: border-color 160ms ease, color 160ms ease;
        }
        .event-share__copy:hover,
        .event-share__copy:focus-visible {
          border-color: var(--share-tint);
          color: #fff;
        }

        /* Five across is too tight for a phone; the labels wrap into two rows. */
        @media (max-width: 420px) {
          .event-share__row { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
      `}</style>
    </div>
  );
}
